import type { Vec3 } from '../../../../geo/types';
import type { NearDetailSet } from '../../../mesh/nearDetailLod';
import { TRACERY_KINDS, traceryParts, type TraceryKind, type TraceryPart } from './traceryLayout';

/** A pointed window in the world: its left jamb's foot on the glass, the wall's run to the right seen from outside, and out of the wall. */
export interface PlacedWindow {
  readonly origin: Vec3;
  readonly right: Vec3;
  readonly outward: Vec3;
  readonly width: number;
  /** From the sill to the springing of the arch. */
  readonly rise: number;
}

export interface TracerySink {
  add(window: PlacedWindow, edgeId: number): void;
}

/** Every traceried window's pieces, one set of instance matrices per kind. */
export interface TraceryCollector extends TracerySink {
  /** The windows that took tracery, in the order they came. */
  readonly windows: readonly PlacedWindow[];
  finish(): readonly NearDetailSet<TraceryKind>[];
}

/** For a build that keeps no tracery: its windows keep their reveals. */
export const NO_TRACERY: TracerySink = { add: () => undefined };

interface Growing {
  readonly matrices: number[];
  readonly edgeIds: number[];
}

/* Column-major: the template's x runs along the wall, y up and z out of the glass, each stretched by the part. */
function writePart(out: number[], w: PlacedWindow, p: TraceryPart): void {
  const { origin: o, right: r, outward: n } = w;
  out.push(
    r[0] * p.across, r[1] * p.across, r[2] * p.across, 0,
    0, p.up, 0, 0,
    n[0] * p.out, n[1] * p.out, n[2] * p.out, 0,
    o[0] + r[0] * p.x, o[1] + r[1] * p.x + p.y, o[2] + r[2] * p.x, 1
  );
}

export function createTraceryCollector(): TraceryCollector {
  const byKind = new Map<TraceryKind, Growing>(TRACERY_KINDS.map((k) => [k, { matrices: [], edgeIds: [] }]));
  const windows: PlacedWindow[] = [];
  return {
    windows,
    add(window, edgeId) {
      const parts = traceryParts(window.width, window.rise);
      if (parts.length === 0) return;
      windows.push(window);
      for (const part of parts) {
        const target = byKind.get(part.kind)!;
        writePart(target.matrices, window, part);
        target.edgeIds.push(edgeId);
      }
    },
    finish() {
      return TRACERY_KINDS.map((kind) => {
        const { matrices, edgeIds } = byKind.get(kind)!;
        return { kind, count: edgeIds.length, matrices: Float32Array.from(matrices), edgeIds: Float32Array.from(edgeIds) };
      });
    },
  };
}
